import React from "react";
import { Skeleton } from "antd";
import {
  BankOutlined,
  CheckCircleFilled,
  EnvironmentOutlined,
  IdcardOutlined,
  LockOutlined,
  StopOutlined,
  SwapOutlined,
  TeamOutlined,
} from "@ant-design/icons";

const fmt = n => Number(n || 0).toLocaleString("fr-CH");

/** The sentence with the target name in bold wherever it appears. */
function Sentence({ text, target }) {
  if (!target || !text.includes(target)) return <>{text}</>;
  const parts = text.split(target);
  return (
    <>
      {parts.map((p, i) => (
        <React.Fragment key={i}>
          {p}
          {i < parts.length - 1 && <strong>{target}</strong>}
        </React.Fragment>
      ))}
    </>
  );
}

/** Our own sentence when the server sends none — same facts, same words. */
export const buildSentence = ({
  sources,
  target,
  records,
  accounts,
  addresses,
  responsables,
  kept,
  dropped,
  existing,
}) => {
  const s = [];
  s.push(
    existing
      ? `${sources} établissement${sources > 1 ? "s" : ""} rejoigne${
          sources > 1 ? "nt" : ""
        } ${target}.`
      : `${sources} établissements deviennent 1 seul : ${target}.`,
  );
  if (records)
    s.push(
      `${fmt(records)} fiche${
        records > 1 ? "s changent" : " change"
      } d'établissement.`,
    );
  const dup = [
    accounts ? `${accounts} compte${accounts > 1 ? "s" : ""}` : null,
    addresses ? `${addresses} adresse${addresses > 1 ? "s" : ""}` : null,
    responsables
      ? `${responsables} responsable${responsables > 1 ? "s" : ""}`
      : null,
  ].filter(Boolean);
  if (dup.length) {
    const last = dup.pop();
    s.push(
      `${dup.length ? `${dup.join(", ")} et ${last}` : last} en double ${
        (accounts || 0) + (addresses || 0) + (responsables || 0) > 1
          ? "sont fusionnés"
          : "est fusionné"
      }.`,
    );
  }
  if (kept)
    s.push(
      `${kept} exclusion${kept > 1 ? "s" : ""} d'interprète${
        kept > 1 ? "s sont gardées" : " est gardée"
      }.`,
    );
  if (dropped)
    s.push(
      `${dropped} exclusion${
        dropped > 1 ? "s sont retirées" : " est retirée"
      }.`,
    );
  s.push("Rien n'est encore écrit.");
  return s.join(" ");
};

function Tile({ icon: Icon, value, label, tone, loading }) {
  return (
    <div className={`sv-tile sv-k-${tone}`}>
      <div className="sv-tile-icon">
        <Icon />
      </div>
      <div>
        <div className="sv-tile-value">
          {loading ? <Skeleton.Button active size="small" /> : fmt(value)}
        </div>
        <div className="sv-tile-label">{label}</div>
      </div>
    </div>
  );
}

/**
 * « Résumé en une phrase »: what the merge does, in words a child understands, then the big
 * numbers, then the reassurance line (nothing stays behind, nothing is written yet).
 */
export function SummaryCard({ sentence, target, facts, loading, residual }) {
  const text = sentence || buildSentence({ ...facts, target });
  return (
    <div className="sv-summary">
      <div className="sv-summary-kicker">Résumé en une phrase</div>
      <div className="sv-summary-sentence">
        {loading && !sentence ? (
          <Skeleton active title={false} paragraph={{ rows: 2 }} />
        ) : (
          <Sentence text={text} target={target} />
        )}
      </div>
      <div className="sv-tiles">
        <Tile
          icon={BankOutlined}
          tone="archived"
          value={facts.sources}
          label="établissements archivés"
        />
        <Tile
          icon={SwapOutlined}
          tone="id_replaced"
          value={facts.records}
          label="fiches changent d'établissement"
          loading={loading && !facts.records}
        />
        <Tile
          icon={TeamOutlined}
          tone="merged"
          value={facts.accounts}
          label="comptes fusionnés"
          loading={facts.loadingEntities}
        />
        <Tile
          icon={EnvironmentOutlined}
          tone="merged"
          value={facts.addresses}
          label="adresses fusionnées"
          loading={facts.loadingEntities}
        />
        <Tile
          icon={IdcardOutlined}
          tone="merged"
          value={facts.responsables}
          label="responsables fusionnés"
          loading={facts.loadingEntities}
        />
        <Tile
          icon={StopOutlined}
          tone="id_replaced"
          value={facts.kept}
          label={
            facts.dropped
              ? `exclusions gardées · ${facts.dropped} retirée${
                  facts.dropped > 1 ? "s" : ""
                }`
              : "exclusions gardées"
          }
          loading={facts.loadingEntities}
        />
      </div>
      <div className="sv-summary-foot">
        <span className="sv-summary-check">
          <CheckCircleFilled /> Reste sur les établissements : {fmt(residual)}
        </span>
        <span className="sv-summary-note">
          — plus aucune fiche ne pointera vers un ancien établissement.
        </span>
        <span className="sv-summary-lock">
          <LockOutlined /> Rien n'est encore écrit : la base ne change qu'au
          moment où vous appliquez.
        </span>
      </div>
    </div>
  );
}
